import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Radio, ArrowRight } from "lucide-react";
import { KineticHeading } from "@/components/common/KineticHeading";
import { MEDIA } from "@/config/media";

export const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={sectionRef} data-testid="hero-section" className="relative flex min-h-[92vh] items-center overflow-hidden bg-brand-black">
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY }} initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}>
        <img src={MEDIA.heroBackground.url} alt={MEDIA.heroBackground.alt} className="h-full w-full object-cover opacity-60" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/70 to-brand-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-black/85 via-transparent to-brand-black/50" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 py-32 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          data-testid="hero-eyebrow"
          className="mb-6 inline-flex items-center gap-2 border border-brand-purple/60 px-4 py-1.5 font-subheading text-xs font-semibold uppercase tracking-[0.2em] text-brand-purple"
        >
          <Radio className="h-3 w-3" /> Grassroots Women&apos;s Football Coverage
        </motion.p>

        <h1 className="font-heading uppercase leading-[1.05] text-white">
          <KineticHeading lines={["WomensMatchDay", "Live"]} className="text-5xl sm:text-7xl md:text-8xl" delay={0.2} />
        </h1>

        <div className="mt-8 max-w-xl overflow-hidden">
          <motion.p initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ delay: 0.6, duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="font-subheading text-2xl text-white/90">
            Text goes here
          </motion.p>
        </div>
        <div className="mt-4 max-w-lg overflow-hidden">
          <motion.p initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ delay: 0.75, duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="text-base text-white/60">
            Text goes here
          </motion.p>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.6 }} className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            data-testid="hero-watch-live-btn"
            to="/live"
            className="group inline-flex items-center gap-2 bg-brand-purple px-7 py-4 font-subheading text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-brand-purple-hover"
          >
            Watch Live <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            data-testid="hero-follow-us-btn"
            to="/socials"
            className="inline-flex items-center gap-2 border border-white/30 px-7 py-4 font-subheading text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:border-white hover:bg-white/10"
          >
            Follow Us
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-[11px] uppercase tracking-widest text-white/40"
      >
        Scroll
      </motion.div>
    </section>
  );
};
